<template>
  <div :id="mapId" class="w-100 map"></div>
</template>

<script>
import Map from 'ol/Map';
import View from 'ol/View';
import Collection from 'ol/Collection';
import Feature from 'ol/Feature';
import { Point, LineString, Polygon } from 'ol/geom';
import * as condition from 'ol/events/condition';
import
{
  Draw,
  Modify,
  Select,
  defaults as DefaultInteractions,
} from 'ol/interaction';
import {
  Image as ImageLayer,
  Tile as TileLayer,
  Vector as VectorLayer,
  Group as LayerGroup,
} from 'ol/layer';
import * as olExtent from 'ol/extent';
import
{
  TileArcGISRest,
  ImageArcGISRest,
  XYZ,
  Vector as VectorSource,
} from 'ol/source';
import { mapState } from 'vuex';
import MapUtilities from '@/mixins/MapUtilities';

const styles = {
  harvest: {
    strokeColor: 'rgb(0, 178, 207)',
    fillColor: 'rgba(0, 178, 207, 0.4)',
  },
  road: {
    strokeColor: 'rgb(255,77,215)',
    lineDash: [10, 5],
  },
  crossing: {
    strokeColor: 'rgb(255, 128, 0)',
    fillColor: 'rgb(200, 200, 200, 0.2)',
  },
};

export default {
  name: 'Map',
  props: {
    mapId: {
      type: String,
      required: true,
    },
    extent: {
      type: Array,
      required: true,
    },
    layerSwitcherEnabled: {
      type: Boolean,
      default: true,
    },
    geocoderEnabled: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [MapUtilities],
  data() {
    return {
      map: null,
      drawInteraction: null,
      editCollection: new Collection(),
      harvestStyle: this.createStyleFunction(styles.harvest),
      harvestEditStyle: this.createEditStyleFunction(styles.harvest),
      roadStyle: this.createStyleFunction(styles.road),
      roadEditStyle: this.createEditStyleFunction(styles.road),
      crossingStyle: this.createPointStyleFunction(styles.crossing),
      crossingEditStyle: this.createPointEditStyleFunction(styles.crossing),
      harvestModify: null,
      roadModify: null,
      crossingModify: null,
      selectClick: new Select({
        condition: condition.click,
        style: false,
      }),
      showGeocoder: this.geocoderEnabled, // Local copy to avoid mutating prop
    };
  },
  computed: {
    ...mapState('Configuration', {
      basemapLayers: state => state.configuration.mapConfiguration.basemapLayers,
      referenceLayers: state => state.configuration.mapConfiguration.referenceLayers,
    }),
    ...mapState('Activity', {
      activityTypes: state => state.activityTypes,
      harvestSource: state => state.harvestSource,
      roadConstructionSource: state => state.roadConstructionSource,
      waterCrossingSource: state => state.waterCrossingSource,
    }),
  },
  mounted() {
    const webMercatorWorldExtent = [-13884991, 2870341, -7455066, 6338219];
    const webMercatorMaineExtent = [-8030363.23, 5294473.91, -7434110.66, 6013961.93];
    this.map = new Map({
      target: this.mapId,
      interactions: new DefaultInteractions({
        doubleClickZoom: false,
        altShiftDragRotate: false,
        pinchRotate: false,
      }),
      layers: [
        new LayerGroup({
          title: 'Basemaps',
          toggle: 'radio',
          layers: [
            ...this.basemapLayers.map(bml => new TileLayer({
              title: bml.title,
              visible: bml.default,
              source: bml.source && bml.source === 'xyz'
                ? new XYZ({
                  attributions: [bml.attribution],
                  extent: webMercatorWorldExtent,
                  url: bml.url,
                })
                : new TileArcGISRest({
                  attributions: [bml.attribution],
                  extent: webMercatorWorldExtent,
                  url: bml.url,
                }),
            })),
          ],
        }),
        new LayerGroup({
          title: 'Reference',
          toggle: 'checkbox',
          layers: [
            ...this.referenceLayers.map((rl) => {
              const params = {};
              if (rl.layers) {
                params.LAYERS = `show:${rl.layers.reduce((csv, layerNumber) => `${csv},${layerNumber}`)}`;
              }
              if (rl.dynamicLayers) {
                params.DYNAMICLAYERS = rl.dynamicLayers;
                return new ImageLayer({
                  title: rl.title,
                  visible: rl.default,
                  legendLayers: rl.layers,
                  source: new ImageArcGISRest({
                    attributions: [rl.attribution],
                    extent: webMercatorWorldExtent,
                    url: rl.url,
                    params,
                  }),
                });
              }
              return new TileLayer({
                title: rl.title,
                visible: rl.default,
                legendLayers: rl.layers,
                source: new TileArcGISRest({
                  attributions: [rl.attribution],
                  extent: webMercatorWorldExtent,
                  url: rl.url,
                  params,
                }),
              });
            }),
          ],
        }),
        new LayerGroup({
          title: 'Activities',
          layers: [
            new VectorLayer({
              title: 'Harvest',
              source: this.harvestSource,
              style: this.harvestStyle,
            }),
            new VectorLayer({
              title: 'Road Construction',
              source: this.roadConstructionSource,
              style: this.roadStyle,
            }),
            new VectorLayer({
              title: 'Water Crossing',
              source: this.waterCrossingSource,
              style: this.crossingStyle,
            }),
          ],
        }),
      ],
      view: new View({
        center: olExtent.getCenter(webMercatorMaineExtent),
        extent: webMercatorMaineExtent,
        constrainOnlyCenter: true,
        minZoom: 7,
        maxZoom: 19,
        zoom: 7,
      }),
    });

    // Hide map search tool (note: this was always hiding it regardless of prop)
    // this.geocoderEnabled = false;  // REMOVED: Don't mutate prop

    // Add event for select interaction
    this.selectClick.on('select', (e) => {
      const first = e.selected[0];
      this.$emit('activityClicked', first.getId());

      // Clear the selected features collection after emitting event.
      // This effectively disables the "toggle" functionality of the ol select
      e.target.getFeatures().clear();
    });

    // Add the click interaction immediately.
    // Note it should be removed and re-added when trying or modifying features to prevent jank.
    this.map.addInteraction(this.selectClick);

    this.map.getView().fit(this.extent);

    this.harvestModify = new Modify({
      features: this.editCollection,
      style: this.harvestEditStyle,
      deleteCondition: this.deleteCondition,
    });
    this.roadModify = new Modify({
      features: this.editCollection,
      style: this.roadEditStyle,
      deleteCondition: this.deleteCondition,
    });

    this.crossingModify = new Modify({
      features: this.editCollection,
      style: this.crossingEditStyle,
    });

    // Add controls from mixin
    if (this.showGeocoder) this.map.addControl(this.createGeocoderControl());
    this.map.addControl(this.createCenterMapControl(this.extent));
    if (this.layerSwitcherEnabled) this.map.addControl(this.createLayerSwitcherControl());
    this.map.addControl(this.createScaleLineControl());

    // Set up layer visibility tracking
    this.$nextTick(() => {
      this.setupLayerVisibilityTracking();
      // Apply initial visibility from store
      const storedVisibility = this.$store.state.Map.layerVisibility;
      if (Object.keys(storedVisibility).length > 0) {
        this.applyLayerVisibility(storedVisibility);
      }
    });
  },
  watch: {
    // Watch for layer visibility changes from store
    '$store.state.Map.layerVisibility': {
      handler(newVisibility) {
        this.applyLayerVisibility(newVisibility);
      },
      deep: true,
    },
  },
  methods: {
    // Apply visibility from store to map layers
    applyLayerVisibility(visibility) {
      if (!this.map) return;

      this.map.getLayers().forEach((layerGroup) => {
        if (layerGroup.getLayers) {
          const isRadioGroup = layerGroup.get('toggle') === 'radio';
          
          layerGroup.getLayers().forEach((layer) => {
            const title = layer.get('title');
            if (title && visibility.hasOwnProperty(title)) {
              layer.setVisible(visibility[title]);
              
              // Update the checkbox/radio button in the UI
              const toggle = document.getElementById(`toggle-${layer.ol_uid}`);
              if (toggle) {
                if (isRadioGroup) {
                  // For radio buttons, set checked state
                  toggle.checked = visibility[title];
                } else {
                  // For checkboxes, set checked state
                  toggle.checked = visibility[title];
                  
                  // Trigger change event to update legend visibility
                  const event = new Event('change');
                  toggle.dispatchEvent(event);
                }
              }
            }
          });
        }
      });
    },

    // Capture current layer visibility and save to store
    captureLayerVisibility() {
      if (!this.map) return;

      const visibility = {};
      this.map.getLayers().forEach((layerGroup) => {
        if (layerGroup.getLayers) {
          layerGroup.getLayers().forEach((layer) => {
            const title = layer.get('title');
            if (title) {
              visibility[title] = layer.getVisible();
            }
          });
        }
      });

      this.$store.dispatch('Map/setAllLayerVisibility', visibility);
    },

    // Set up listeners for layer visibility changes
    setupLayerVisibilityTracking() {
      if (!this.map) return;

      this.map.getLayers().forEach((layerGroup) => {
        if (layerGroup.getLayers) {
          layerGroup.getLayers().forEach((layer) => {
            layer.on('change:visible', () => {
              this.captureLayerVisibility();
            });
          });
        }
      });
    },

    deleteCondition(evt) {
      if ((condition.touchOnly(evt) || condition.penOnly(evt))
        && condition.doubleClick(evt)) {
        return true;
      }
      if (condition.mouseOnly(evt)
        && evt.originalEvent.altKey
        && condition.singleClick(evt)) {
        return true;
      }
      return false;
    },
    resizeMap() {
      this.map.updateSize();
    },
    zoomToFeature(type, id) {
      let featSource = null;
      switch (type) {
        case this.activityTypes.harvest.name:
          featSource = this.harvestSource;
          break;

        case this.activityTypes.roadConstruction.name:
          featSource = this.roadConstructionSource;
          break;

        case this.activityTypes.waterCrossing.name:
          featSource = this.waterCrossingSource;
          break;

        default:
          break;
      }

      if (featSource) {
        const feat = featSource.getFeatures().find(f => f.getId() === id);
        if (feat) {
          this.map.getView().fit(feat.getGeometry().getExtent());
        }
      }
    },
    addDrawInt() {
      this.map.removeInteraction(this.selectClick);
      this.map.addInteraction(this.drawInteraction);
    },
    removeDrawInt() {
      this.map.removeInteraction(this.drawInteraction);
      this.map.addInteraction(this.selectClick);
    },
    drawHarvest(id) {
      this.drawInteraction = new Draw({
        source: new VectorSource({ wrapX: false }),
        type: 'Polygon',
        style: this.harvestEditStyle,
        minPoints: 3,
        freehandCondition: condition.never,
      });

      this.drawInteraction.on('drawstart', (evt) => {
        evt.feature.setId(id);
        this.createMeasurementTooltip(evt.feature);
      });

      this.drawInteraction.on('drawend', (evt) => {
        this.harvestSource.addFeature(evt.feature);

        this.removeDrawInt();

        this.modifyFeature(
          evt.feature,
          this.harvestSource,
          this.harvestEditStyle,
          this.harvestModify,
        );
        this.$store.dispatch('Activity/switchToEdit');
      });
      this.addDrawInt();
    },
    loadFeatures(nextId, features, activityType) {
      const load = (features, startId, source, style, modify) => {
        let fid = startId;
        features.forEach((feature) => {
          feature.setId(fid);
          source.addFeature(feature);
          this.modifyFeature(
            feature,
            source,
            style,
            modify,
          );
          fid += 1;
        });
      };

      switch (activityType.name) {
        case this.activityTypes.harvest.name:
          load(features.map(f => new Feature({
            geometry: new Polygon(f.geometry.coordinates),
          })), nextId, this.harvestSource, this.harvestEditStyle, this.harvestModify);
          break;
        case this.activityTypes.roadConstruction.name:
          load(features.map(f => new Feature({
            geometry: new LineString(f.geometry.coordinates),
          })), nextId, this.roadConstructionSource, this.roadEditStyle, this.roadModify);
          break;
        case this.activityTypes.waterCrossing.name:
          load(features.map(f => new Feature({
            geometry: new Point(f.geometry.coordinates),
          })), nextId, this.waterCrossingSource, this.crossingEditStyle, this.crossingModify);
          break;
        default:
          break;
      }
    },
    drawRoadConstruction(id) {
      this.drawInteraction = new Draw({
        source: new VectorSource({ wrapX: false }),
        type: 'LineString',
        style: this.roadEditStyle,
        minPoints: 2,
        freehandCondition: condition.never,
      });

      this.drawInteraction.on('drawstart', (evt) => {
        evt.feature.setId(id);
        this.createMeasurementTooltip(evt.feature);
      });

      this.drawInteraction.on('drawend', (evt) => {
        this.roadConstructionSource.addFeature(evt.feature);

        this.removeDrawInt();

        this.modifyFeature(
          evt.feature,
          this.roadConstructionSource,
          this.roadEditStyle,
          this.roadModify,
        );
        this.$store.dispatch('Activity/switchToEdit');
      });
      this.addDrawInt();
    },
    drawWaterCrossing(id) {
      this.drawInteraction = new Draw({
        source: new VectorSource({ wrapX: false }),
        type: 'Point',
        style: this.crossingEditStyle,
      });

      this.drawInteraction.on('drawstart', (evt) => {
        evt.feature.setId(id);
      });

      this.drawInteraction.on('drawend', (evt) => {
        this.waterCrossingSource.addFeature(evt.feature);

        this.removeDrawInt();

        this.modifyFeature(
          evt.feature,
          this.waterCrossingSource,
          this.crossingEditStyle,
          this.crossingModify,
        );

        this.$store.dispatch('Activity/switchToEdit');
      });
      this.addDrawInt();
    },
    modifyFeature(feature, source, style, interaction) {
      feature.setStyle(style);
      this.map.removeInteraction(this.selectClick);
      this.map.addInteraction(interaction);
      this.editCollection.push(feature);
    },
    saveActivity(activityId) {
      const feature = this.editCollection.getArray().find(f => f.getId() === activityId);
      return this.$store.dispatch('Activity/saveActivity', feature)
        .then(() => {
          this.cleanUpDrawing(feature.getId());
        });
    },
    cancelDrawing(activityId) {
      this.destroyMeasurementTooltip();

      this.removeDrawInt();

      [this.harvestSource, this.roadConstructionSource, this.waterCrossingSource]
        .forEach((source) => {
          const feature = source.getFeatureById(activityId);
          if (feature) source.removeFeature(feature);
        });

      this.drawInteraction = null;
      this.cleanUpDrawing(activityId);
    },
    cleanUpDrawing(activityId) {
      if (this.editCollection.getLength()) {
        const feature = this.editCollection.getArray().find(f => f.getId() === activityId);
        if (feature) {
          this.editCollection.remove(feature);
          feature.setStyle(null);
        }
      }
      // there may be other features in the editCollection from the shapefile upload
      if (this.editCollection.getLength() > 0) {
        return;
      }

      this.map.removeControl(this.harvestHelpControl);
      this.map.removeControl(this.roadHelpControl);
      this.map.removeControl(this.crossingHelpControl);
      this.map.removeInteraction(this.harvestModify);
      this.map.removeInteraction(this.roadModify);
      this.map.removeInteraction(this.crossingModify);
      this.map.addInteraction(this.selectClick);
      this.destroyMeasurementTooltip();
    },
  },
};
</script>
